import { livescoreRuntime } from '../../shared/runtime/livescore-runtime';
import { mountPanelPlayer } from './panel-player';
import { panelActions, type PanelTab } from './panel-markup';
import { empty, object, renderStats, renderIncidents, renderLineup, renderStandings, renderMarkets, type Item } from './panel-data';

export type PanelContract = { matchEndpoint?: string; competitionEndpoint?: string; simulationUrl?: string };
type Context = { company: string; format: string; matches: Record<string, any> };
let panelSerial = 0;

/** One expansion per board; refresh is driven by the existing board lifecycle. */
export const mountMatchPanel = (root: HTMLElement, contract: PanelContract, context: () => Context) => {
  let host: HTMLElement | null = null;
  let row: HTMLElement | null = null;
  let matchId = '';
  let active: PanelTab | null = null;
  let controller: AbortController | null = null;
  let epoch = 0;
  let lastFetch = 0;
  let lastContext = '';
  let pendingContext = '';
  let data: Item = {};
  let lineupTab: 'pitch' | 'home' | 'away' = 'pitch';
  let player: ReturnType<typeof mountPanelPlayer> | null = null;
  let streamController: AbortController | null = null;
  let streamFetched = 0;
  let disposed = false;
  const view = (key: string) => host?.querySelector<HTMLElement>(`[data-gwd-odds-panel-view="${key}"]`) ?? null;
  const emit = () => root.dispatchEvent(new CustomEvent('gwd:odds-panel-change', { bubbles: true, detail: { matchId, tab: active, open: Boolean(active), panel: host } }));
  const loadStream = async (force = false) => {
    if (!host || !active || active === 'markets' || !player || streamController || document.hidden || !navigator.onLine) return;
    if (!force && Date.now() - streamFetched < 30000) return;
    const endpoint = contract.matchEndpoint?.replace('{matchId}', encodeURIComponent(matchId));
    if (!endpoint) { player?.fallback(); return; }
    const request = new AbortController(); streamController = request;
    const owner = player;
    try {
      const payload = object(await livescoreRuntime.fetchJson(endpoint, { lang: 'vi', isIncludedRelation: true, returnList: ['info', 'livestreams'] }, request.signal, 'no-cache'));
      if (request.signal.aborted || owner !== player) return;
      if (payload.success === false) throw new Error('Stream API failed');
      owner?.update(object(payload.data ?? payload)); streamFetched = Date.now();
    } catch {
      if (!request.signal.aborted && owner === player) { owner?.fallback(); streamFetched = Date.now(); }
    } finally { if (streamController === request) streamController = null; }
  };
  const close = () => {
    controller?.abort(); controller = null; epoch++;
    streamController?.abort(); streamController = null;
    player?.destroy(); player = null; streamFetched = 0;
    if (host) { host.hidden = true; host.classList.remove('is-markets'); host.querySelectorAll<HTMLElement>('[data-gwd-odds-panel-view]').forEach(node => { node.replaceChildren(); node.hidden = true; }); }
    row?.querySelectorAll<HTMLElement>('[data-gwd-odds-action]').forEach(button => { button.setAttribute('aria-expanded', 'false'); button.classList.remove('is-active'); });
    active = null; emit(); host = null; row = null; matchId = ''; data = {}; lineupTab = 'pitch'; lastFetch = 0; lastContext = ''; pendingContext = '';
  };
  const render = () => {
    if (!host || !row || !active) return;
    const names: [string, string] = [row.querySelector('[data-gwd-home]')?.textContent || 'Đội nhà', row.querySelector('[data-gwd-away]')?.textContent || 'Đội khách'];
    const target = view(active); if (!target) return;
    const ctx = context();
    const html = active === 'stats' ? renderStats(data) : active === 'incidents' ? renderIncidents(data, names) : active === 'lineup' ? renderLineup(data, names, lineupTab) : active === 'standings' ? renderStandings(data) : renderMarkets(data, ctx.company, ctx.format, Number(object(ctx.matches[matchId]?.info).statusId));
    if (target.innerHTML !== html) target.innerHTML = html;
  };
  const load = async (force = false) => {
    if (!active || !host || controller || disposed || document.hidden || !navigator.onLine) return;
    const ctx = context(); const stamp = `${matchId}:${active}:${ctx.company}`;
    if (!force && stamp === lastContext && Date.now() - lastFetch < 30000) return;
    const requestedTab = active; const requestEpoch = ++epoch;
    pendingContext = stamp;
    const request = new AbortController(); controller = request;
    const target = view(requestedTab);
    if (target && !Object.keys(data).length) target.innerHTML = empty('Đang tải…');
    try {
      const competitionId = String(object(ctx.matches[matchId]?.info).competitionId || '');
      if (requestedTab === 'standings' && !competitionId) throw new Error('Missing competition');
      const endpoint = requestedTab === 'standings' ? contract.competitionEndpoint?.replace('{competitionId}', encodeURIComponent(competitionId)) : contract.matchEndpoint?.replace('{matchId}', encodeURIComponent(matchId));
      if (!endpoint) throw new Error('Missing panel endpoint');
      const returns = requestedTab === 'stream' ? ['info', 'livestreams'] : requestedTab === 'standings' ? ['info', 'table'] : requestedTab === 'markets' ? ['asia', 'asiaHt', 'eu', 'euHt', 'bs', 'bsHt', 'cr'] : [requestedTab];
      const payload = object(await livescoreRuntime.fetchJson(endpoint, { lang: 'vi', isIncludedRelation: true, returnList: returns, ...(requestedTab === 'markets' ? { companyIds: ctx.company } : {}) }, request.signal, 'no-cache'));
      if (request.signal.aborted || epoch !== requestEpoch || active !== requestedTab) return;
      if (payload.success === false) throw new Error('Panel API failed');
      data = object(payload.data ?? payload); lastFetch = Date.now(); lastContext = stamp; render();
    } catch (error) {
      if (request.signal.aborted || epoch !== requestEpoch) return;
      // Never leave old prices visible after a failed market refresh.
      if (target) target.innerHTML = empty('Không tải được nội dung. Bấm Thử lại để tải lại.') + '<button type="button" data-odds-panel-retry>Thử lại</button>';
      data = {}; lastFetch = Date.now(); lastContext = stamp;
    } finally { if (controller === request) controller = null; }
  };
  const sync = () => {
    if (!active || !host || !row) return;
    if (!root.contains(host) || !root.contains(row) || !context().matches[matchId]) { close(); return; }
    row.querySelectorAll<HTMLElement>('[data-gwd-odds-action]').forEach(button => { const selected = (button.dataset.gwdOddsAction === 'stream' && active !== 'markets') || button.dataset.gwdOddsAction === active; button.setAttribute('aria-expanded', String(selected)); button.setAttribute('aria-controls', host!.id); button.classList.toggle('is-active', selected); });
    if (active === 'markets' && (controller ? pendingContext : lastContext) && (controller ? pendingContext : lastContext) !== `${matchId}:${active}:${context().company}`) { controller?.abort(); controller = null; epoch++; data = {}; lastContext = ''; }
    if (active === 'markets' && Object.keys(data).length) render();
    void load(); void loadStream();
  };
  const onClick = (event: MouseEvent) => {
    const target = event.target instanceof Element ? event.target : null;
    const lineupButton = target?.closest<HTMLElement>('[data-odds-lineup-tab]');
    if (lineupButton && host?.contains(lineupButton) && active === 'lineup') {
      const next = lineupButton.dataset.oddsLineupTab;
      if (next === 'pitch' || next === 'home' || next === 'away') {
        lineupTab = next; render();
        view('lineup')?.querySelector<HTMLElement>(`[data-odds-lineup-tab="${next}"]`)?.focus({ preventScroll: true });
      }
      return;
    }
    if (target?.closest('[data-odds-panel-retry]')) { void load(true); return; }
    const button = target?.closest<HTMLElement>('[data-gwd-odds-action]');
    const key = button?.dataset.gwdOddsAction;
    if (!button || !key || !Object.prototype.hasOwnProperty.call(panelActions, key)) return;
    const nextRow = button.closest<HTMLElement>('[data-gwd-odds-match-id]');
    const nextHost = nextRow?.querySelector<HTMLElement>('[data-gwd-odds-panel]');
    if (!nextRow || !nextHost || !nextHost.querySelector(`[data-gwd-odds-panel-view="${key}"]`)) return;
    const nextIsMarkets = key === 'markets';
    if (host === nextHost && ((key === 'stream' && active !== 'markets') || (nextIsMarkets && active === 'markets'))) { close(); return; }
    if (host !== nextHost || nextIsMarkets !== (active === 'markets')) {
      close(); row = nextRow; host = nextHost; matchId = row.dataset.gwdOddsMatchId || '';
      const stream = view('stream');
      if (stream && !nextIsMarkets) player = mountPanelPlayer(stream, contract.simulationUrl?.replace('{matchId}', encodeURIComponent(matchId)) || '');
    } else {
      controller?.abort(); controller = null; epoch++;
    }
    active = key === 'stream' ? 'stats' : key as PanelTab;
    if (!view(active)) active = (Object.keys(panelActions).find(tab => tab !== 'stream' && tab !== 'markets' && view(tab)) as PanelTab) || 'stats';
    data = {}; lastFetch = 0; lastContext = '';
    host!.hidden = false; host!.id ||= `gwd-odds-panel-${++panelSerial}`;
    host!.classList.toggle('is-markets', active === 'markets');
    host!.querySelectorAll<HTMLElement>('.gwd-odds-panel__tabs').forEach(node => { node.hidden = active === 'markets'; });
    host!.querySelectorAll<HTMLElement>('[data-gwd-odds-panel-view]').forEach(node => { node.hidden = node.dataset.gwdOddsPanelView !== active && !(active !== 'markets' && node.dataset.gwdOddsPanelView === 'stream'); });
    sync(); emit();
  };
  const visibility = () => {
    if (document.hidden) {
      player?.destroy(); streamFetched = 0;
      streamController?.abort(); streamController = null;
      controller?.abort(); controller = null; epoch++;
    } else if (active) { void loadStream(true); void load(true); }
  };
  const resize = () => { if (row && !row.getClientRects().length) close(); };
  root.addEventListener('click', onClick);
  document.addEventListener('visibilitychange', visibility);
  window.addEventListener('resize', resize);
  const observer = new MutationObserver(() => {
    if (root.isConnected) return;
    disposed = true; close(); observer.disconnect(); root.removeEventListener('click', onClick); document.removeEventListener('visibilitychange', visibility); window.removeEventListener('resize', resize);
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  return { sync, close };
};
